import { useState, useEffect, useCallback } from "react";
import { useRecordContext, useNotify } from "react-admin";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { API_URL, getAuthHeaders } from "../dataProvider";

interface UserRecord {
  id: string;
  email: string;
  role: string;
}

interface AccessUser {
  id: string;
  email: string;
}

export function ProjectAccessPanel() {
  const record = useRecordContext();
  const notify = useNotify();

  const [accessUsers, setAccessUsers] = useState<AccessUser[]>([]);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string>("");

  const projectId = record?.id as string | undefined;

  const fetchAccessUsers = useCallback(async () => {
    if (!projectId) return;
    setLoadingAccess(true);
    setError("");
    try {
      const response = await fetch(
        `${API_URL}/admin/projects/${projectId}/access`,
        {
          headers: getAuthHeaders() as Record<string, string>,
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to load access list (${response.status})`);
      }
      const data = (await response.json()) as AccessUser[];
      setAccessUsers(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load access list";
      setError(message);
    } finally {
      setLoadingAccess(false);
    }
  }, [projectId]);

  const fetchAllUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(
        `${API_URL}/admin/users?_start=0&_end=500&_sort=email&_order=ASC`,
        {
          headers: getAuthHeaders() as Record<string, string>,
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to load users (${response.status})`);
      }
      const data = (await response.json()) as UserRecord[];
      setAllUsers(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load users";
      console.error("ProjectAccessPanel: fetchAllUsers error:", message);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    void fetchAccessUsers();
    void fetchAllUsers();
  }, [fetchAccessUsers, fetchAllUsers]);

  const handleAddUser = useCallback(async () => {
    if (!projectId || !selectedUser) return;
    setAddingUser(true);
    setError("");
    try {
      const response = await fetch(
        `${API_URL}/admin/projects/${projectId}/access`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(getAuthHeaders() as Record<string, string>),
          },
          body: JSON.stringify({ userId: selectedUser.id }),
        }
      );
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(body.message ?? `Failed to grant access (${response.status})`);
      }
      notify(`Accesso concesso a ${selectedUser.email}`, { type: "success" });
      setSelectedUser(null);
      await fetchAccessUsers();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to grant access";
      setError(message);
      notify(`Errore: ${message}`, { type: "error" });
    } finally {
      setAddingUser(false);
    }
  }, [projectId, selectedUser, notify, fetchAccessUsers]);

  const handleRemoveUser = useCallback(
    async (userId: string, userEmail: string) => {
      if (!projectId) return;
      setRemovingUserId(userId);
      setError("");
      try {
        const response = await fetch(
          `${API_URL}/admin/projects/${projectId}/access/${userId}`,
          {
            method: "DELETE",
            headers: getAuthHeaders() as Record<string, string>,
          }
        );
        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as {
            message?: string;
          };
          throw new Error(
            body.message ?? `Failed to revoke access (${response.status})`
          );
        }
        notify(`Accesso revocato per ${userEmail}`, { type: "success" });
        await fetchAccessUsers();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to revoke access";
        setError(message);
        notify(`Errore: ${message}`, { type: "error" });
      } finally {
        setRemovingUserId(null);
      }
    },
    [projectId, notify, fetchAccessUsers]
  );

  if (!record) return null;

  // Users available to add (not already in the access list)
  const accessUserIds = new Set(accessUsers.map((u) => u.id));
  const availableUsers = allUsers.filter((u) => !accessUserIds.has(u.id));

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Accesso utenti
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Gestisci quali utenti hanno accesso a questo progetto.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Current access list */}
      {loadingAccess ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Caricamento lista accessi...</Typography>
        </Box>
      ) : (
        <>
          {accessUsers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Nessun utente ha ancora ricevuto l'accesso.
            </Typography>
          ) : (
            <List dense sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
              {accessUsers.map((user) => (
                <ListItem key={user.id} divider>
                  <ListItemText primary={user.email} secondary={`ID: ${user.id}`} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label={`Rimuovi accesso per ${user.email}`}
                      onClick={() => void handleRemoveUser(user.id, user.email)}
                      disabled={removingUserId === user.id}
                      size="small"
                    >
                      {removingUserId === user.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}

      {/* Add user section */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Autocomplete
          options={availableUsers}
          getOptionLabel={(option) => option.email}
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          loading={loadingUsers}
          sx={{ flexGrow: 1 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Aggiungi utente"
              placeholder="Cerca per email..."
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="contained"
          startIcon={
            addingUser ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />
          }
          onClick={() => void handleAddUser()}
          disabled={!selectedUser || addingUser}
          sx={{ whiteSpace: "nowrap", mt: 0.25 }}
        >
          Concedi accesso
        </Button>
      </Box>
    </Box>
  );
}
